import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { company, products, productCategories } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productOfInterest: '',
    customProduct: '',
    message: '',
    website: '' // honeypot — must stay empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Resolve final product label (custom text when "Other" is selected)
    const productLabel =
      formData.productOfInterest === 'Other'
        ? formData.customProduct.trim() || 'Other (not specified)'
        : formData.productOfInterest;

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          product: productLabel,
          message: formData.message,
          website: formData.website,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to send message');
      }

      toast({
        title: 'Message Sent!',
        description: "Thank you for contacting us. We'll get back to you shortly.",
      });
      // Fire GA4 lead conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          form_name: 'contact_form',
          product_of_interest: productLabel || '(none)',
          value: 1,
          currency: 'INR',
        });
      }
      setFormData({ name: '', email: '', phone: '', company: '', productOfInterest: '', customProduct: '', message: '', website: '' });
    } catch (err) {
      toast({
        title: 'Could not send message',
        description: err.message || 'Something went wrong. Please try again or email us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title="Contact Stallion Bulktech | Get Industrial Solution Consultation"
        description="Get in touch with Stallion Bulktech for expert consultation on dust suppression, machine guarding, and wear protection systems for your industrial operations."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
            <Mail className="h-4 w-4 text-cyan-600" />
            <p className="text-cyan-600 text-sm font-medium">Get in Touch</p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Contact Us
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our products or services? We help you identify the right machine guarding, dust suppression, and wear resistance solutions tailored to your industry requirements.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-[#f9fafb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Let's Connect</h2>
              <p className="text-base text-gray-600 mb-8 md:mb-10 leading-relaxed">
                Contact us to discuss your machine guarding requirements. Our engineering team will help you identify wear zones, dust-exposure points, and the right execution plan, from manufacturing through on-site installation.
              </p>

              <div className="space-y-6">
                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-semibold mb-1">Our Location</h3>
                        <p className="text-gray-600">{company.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-semibold mb-1">Phone Number</h3>
                        <p className="text-gray-600">{company.phone}</p>
                        <p className="text-gray-600">{company.phone2}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-semibold mb-1">Email Address</h3>
                        <p className="text-gray-600">{company.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-hidden h-64 shadow-lg">
                <iframe
                  title="Stallion Bulktech Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3667.4982547891977!2d72.72416831496796!3d23.17486058483861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395c2b987c6e4e4d%3A0x4c5b0c5b0c5b0c5b!2sGatrad%2C%20Ahmedabad%2C%20Gujarat%20382449!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-cyan-300 transition-all hover:shadow-2xl">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Honeypot — visually hidden, bots fill it, humans don't */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none', height: 0, width: 0 }}
                  />
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label htmlFor="productOfInterest" className="block text-sm font-medium text-gray-700 mb-2">
                      Product of Interest
                    </label>
                    <select
                      id="productOfInterest"
                      name="productOfInterest"
                      value={formData.productOfInterest}
                      onChange={handleChange}
                      data-testid="product-of-interest-select"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all appearance-none bg-no-repeat bg-right pr-10"
                      style={{
                        backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
                        backgroundPosition: "right 1rem center",
                      }}
                    >
                      <option value="">— Select a product —</option>
                      {productCategories
                        .filter((cat) => cat !== 'All')
                        .map((category) => {
                          const catProducts = products.filter((p) => p.category === category);
                          if (catProducts.length === 0) return null;
                          return (
                            <optgroup key={category} label={category}>
                              {catProducts.map((p) => (
                                <option key={p.slug} value={p.name}>{p.name}</option>
                              ))}
                            </optgroup>
                          );
                        })}
                      <option value="Other">Other (custom - specify below)</option>
                    </select>
                  </div>

                  {formData.productOfInterest === 'Other' && (
                    <div data-testid="custom-product-input-wrapper">
                      <label htmlFor="customProduct" className="block text-sm font-medium text-gray-700 mb-2">
                        Specify Product / Requirement *
                      </label>
                      <input
                        type="text"
                        id="customProduct"
                        name="customProduct"
                        required
                        value={formData.customProduct}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                        placeholder="e.g. Custom guard for cooling tower drive"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your machine guarding requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group/btn relative w-full"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg blur opacity-50 group-hover/btn:opacity-100 transition-opacity" />
                    <div className="relative w-full px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg transform group-hover/btn:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                      {!isSubmitting && <Send className="h-5 w-5" />}
                    </div>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
